import type { CSSProperties } from "react";

// Canonical hex values — governed by docs/brand/style-guide-v1.md §3.2
// CSS var equivalents noted for future CSS-in-JS migration
//
// #3115 / #3153 lesson — these constants are dark-mode-frozen. Many call
// sites concatenate an alpha suffix (e.g. `${colors.green}44`) which only
// works on 6-char hex literals, so we cannot retro-fit the bridge in place
// without breaking 50+ borders/backgrounds. New code should prefer the
// `var(--brand-*)` tokens directly (see src/styles/brand.css) — they switch
// per-mode. Components that need a hex literal (because they concatenate an
// alpha suffix) should keep using `colors.*` here.
export const colors = {
  bgDeep: "#0f2318",        // --color-bg-secondary
  bgCard: "#1a3c2c",        // --color-bg-card-raised
  bgHeader: "#0d1f14",      // --color-bg-header
  borderDefault: "#3a5c4a", // --color-border
  borderSubtle: "#2a4a3a",  // --color-border-subtle
  goldPrimary: "#F7DC6F",   // --color-amber-light
  goldSecondary: "#F5A623", // --color-amber
  green: "#4caf88",         // --color-status-ok
  textMuted: "#a0b8a8",     // --color-text-secondary
  textBody: "#e0e0e0",      // --color-text-high
  textSubtle: "#c8dfc9",    // --color-text-primary (corrected from #c8d8c8)
  warning: "#e88c00",       // --color-status-warn
  error: "#c94040",         // --color-status-error
} as const;

export const spacing = {
  xs: "4px",
  sm: "8px",
  md: "16px",
  lg: "24px",
  xl: "32px",
  xxl: "48px",
} as const;

export const radii = {
  sm: "6px",
  md: "10px",
  lg: "14px",
  pill: "999px",
} as const;

export const fontSizes = {
  xs: "0.75rem",
  sm: "0.85rem",
  base: "0.95rem",
  md: "1rem",
  lg: "1.15rem",
  xl: "1.4rem",
  xxl: "1.8rem",
  hero: "2.6rem",
} as const;

export const shadows = {
  card: "0 2px 12px rgba(0,0,0,0.35)",
  cardHover: "0 8px 28px rgba(0,0,0,0.55)",
  glow: "0 0 20px rgba(247,220,111,0.15)",
  logo: "0 8px 32px rgba(245,166,35,0.25)",
} as const;

export const transitions = {
  fast: "all 0.15s ease",
  medium: "all 0.22s ease",
} as const;

export const baseInputStyle: CSSProperties = {
  padding: "0.7rem 0.9rem",
  borderRadius: radii.sm,
  border: `1px solid ${colors.borderDefault}`,
  background: colors.bgDeep,
  color: colors.textBody,
  fontSize: "1rem",   // ≥16px — prevents iOS auto-zoom on focus
  outline: "none",
  transition: transitions.fast,
  width: "100%",
  boxSizing: "border-box",
};

export const baseInputFocusStyle: CSSProperties = {
  ...baseInputStyle,
  border: `1px solid ${colors.goldSecondary}`,
  boxShadow: `0 0 0 3px rgba(245,166,35,0.15)`,
};

export const primaryBtnStyle: CSSProperties = {
  padding: "0.78rem 1.5rem",
  minHeight: "44px",
  background: `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})`,
  color: colors.bgCard,
  border: "none",
  borderRadius: radii.sm,
  fontWeight: 700,
  fontSize: fontSizes.base,
  cursor: "pointer",
  letterSpacing: "0.04em",
  transition: transitions.fast,
  width: "100%",
};

export const primaryBtnHoverStyle: CSSProperties = {
  ...primaryBtnStyle,
  background: `linear-gradient(135deg, #e8c94a, #d4891a)`,
  transform: "translateY(-1px)",
};

export const ghostBtnStyle: CSSProperties = {
  padding: "0.78rem 1.5rem",
  background: "none",
  border: `1px solid ${colors.borderDefault}`,
  color: colors.textMuted,
  borderRadius: radii.sm,
  fontWeight: 500,
  fontSize: fontSizes.base,
  cursor: "pointer",
  transition: transitions.fast,
  width: "100%",
};

export const cardStyle: CSSProperties = {
  background: colors.bgCard,
  borderRadius: radii.lg,
  border: `1px solid ${colors.borderDefault}`,
  boxShadow: shadows.card,
};

export const pageWrapStyle: CSSProperties = {
  minHeight: "100vh",
  backgroundColor: colors.bgDeep,
  color: colors.textBody,
  fontFamily: "'Inter', system-ui, sans-serif",
};

export const authCardStyle: CSSProperties = {
  ...cardStyle,
  padding: "2.5rem 2rem",
  width: "min(360px, 92vw)",
  display: "flex",
  flexDirection: "column",
  gap: "1.1rem",
};

export const centeredPageStyle: CSSProperties = {
  ...pageWrapStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
};

// ── Table row colours (admin / fleet / PMO tables) ────────────────────────────
export const TABLE_HEADER_BG = "#0d1f14";
export const ROW_ALT_A       = "#0f2318";
export const ROW_ALT_B       = "#1a3c2c";

